import {
    NOTE_BODY_ROLE,
    NOTE_MARKER_ROLE,
    NOTE_RULE_ROLE,
    OBJ,
} from '../constants';
import { makeShape, makeText, newId } from './objects';

/**
 * Footnotes and endnotes.
 *
 * Each note is three ordinary objects sharing a `noteId`: a superscript marker
 * where the reader is reading, the note itself, and (once per page that has
 * any) the rule above the stack. Nothing new to draw, nothing new to export —
 * but they are numbered and laid out together, because that is the part a
 * person will not do by hand for the fourteenth note on page nine.
 *
 * Footnotes sit at the foot of their own page and count 1, 2, 3. Endnotes
 * gather on the last page and count i, ii, iii — the same split Word makes, and
 * the one that lets a document carry both without two notes sharing a number.
 */

const BOTTOM_MARGIN = 44;
const SIDE_MARGIN = 44;
const NOTE_FONT = 8.5;
const NOTE_ROW = 13;
const MARKER_FONT_RATIO = 0.62;

export const NOTE_KIND = { FOOT: 'footnote', END: 'endnote' };

const ROMAN = [
    [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
];

/** Endnote numbering: i, ii, iii — Word's default, and unmistakable next to a footnote. */
function roman(n) {
    let left = n;
    let out = '';
    for (const [value, numeral] of ROMAN) {
        while (left >= value) { out += numeral; left -= value; }
    }
    return out || String(n);
}

export const labelFor = (kind, index) => (kind === NOTE_KIND.END ? roman(index + 1) : String(index + 1));

/** The label a note body starts with, so a renumber can replace just that. */
const LABEL_PREFIX = /^\s*(?:[0-9]+|[ivxlcdm]+)[.)]\s*/i;
export const noteTextOf = (body) => (body?.text || '').replace(LABEL_PREFIX, '');
const withLabel = (label, text) => `${label}. ${text}`;

/**
 * Make one note: the marker that goes beside the text, and the note itself.
 * Position and numbering are settled afterwards by `layoutNotes`.
 */
export function createNote(page, anchor, settings, kind) {
    const noteId = newId('note');
    const markerSize = Math.max(6, (anchor?.fontSize || settings.fontSize || 12) * MARKER_FONT_RATIO);

    // Beside the anchor if there is one, raised to where a superscript sits;
    // otherwise where the reader is looking, to be dragged into place.
    const markerBox = anchor
        ? {
            x: Math.min(page.width - 20, anchor.x + anchor.w + 1),
            y: anchor.y - markerSize * 0.25,
            w: 16,
            h: markerSize * 1.4,
        }
        : { x: page.width / 2, y: page.height * 0.4, w: 16, h: markerSize * 1.4 };

    return {
        marker: {
            ...makeText(markerBox, { ...settings, fontSize: markerSize }, '1'),
            role: NOTE_MARKER_ROLE,
            noteId,
            noteKind: kind,
        },
        body: {
            ...makeText(
                { x: SIDE_MARGIN, y: page.height - BOTTOM_MARGIN, w: page.width - SIDE_MARGIN * 2, h: NOTE_ROW },
                { ...settings, fontSize: NOTE_FONT, align: 'left' },
                '',
            ),
            role: NOTE_BODY_ROLE,
            noteId,
            noteKind: kind,
        },
    };
}

/** Every marker in the document, in reading order: page, then down, then across. */
export function collectNotes(pages, objectsByUid) {
    const found = [];
    pages.forEach((page, pageIndex) => {
        (objectsByUid[page.uid] || [])
            .filter((obj) => obj.role === NOTE_MARKER_ROLE)
            .sort((a, b) => (a.y - b.y) || (a.x - b.x))
            .forEach((marker) => found.push({ noteId: marker.noteId, kind: marker.noteKind, pageIndex }));
    });
    return found;
}

/**
 * Number every note and put every note body where it belongs.
 *
 * Numbering runs in reading order across the whole document, so deleting note
 * two renumbers three onwards — which is the only behaviour anyone expects and
 * the one nobody wants to do by hand. Bodies are stacked up from the foot of
 * their page, with the rule sitting above whatever the stack came to.
 *
 * @returns {Record<string, object[]>} the new objects-by-page map
 */
export function layoutNotes(pages, objectsByUid) {
    const order = collectNotes(pages, objectsByUid);
    if (!order.length) {
        // The last note has gone: its rule goes with it.
        const cleared = {};
        for (const [uid, list] of Object.entries(objectsByUid)) {
            cleared[uid] = (list || []).filter((obj) => obj.role !== NOTE_RULE_ROLE);
        }
        return cleared;
    }

    const labels = new Map();
    let feet = 0;
    let ends = 0;
    order.forEach((note) => {
        const isEnd = note.kind === NOTE_KIND.END;
        labels.set(note.noteId, labelFor(note.kind, isEnd ? ends : feet));
        if (isEnd) ends += 1; else feet += 1;
    });

    // Where each note's body lives: its own page for a footnote, the last page
    // for an endnote.
    const lastIndex = pages.length - 1;
    const homeOf = new Map(order.map((note) => [
        note.noteId,
        note.kind === NOTE_KIND.END ? lastIndex : note.pageIndex,
    ]));

    const next = {};
    pages.forEach((page) => {
        next[page.uid] = (objectsByUid[page.uid] || []).filter((obj) => obj.role !== NOTE_RULE_ROLE);
    });

    // Bodies may have to move between pages — an endnote's marker can be on
    // page two while its note belongs on page nine — so they are lifted out
    // first and put back where this pass says they go.
    const bodies = new Map();
    pages.forEach((page) => {
        next[page.uid] = next[page.uid].filter((obj) => {
            if (obj.role !== NOTE_BODY_ROLE) return true;
            bodies.set(obj.noteId, obj);
            return false;
        });
        // A marker whose body is gone is not a note any more.
        next[page.uid] = next[page.uid].filter((obj) => (
            obj.role !== NOTE_MARKER_ROLE || bodies.has(obj.noteId) || labels.has(obj.noteId)
        ));
        next[page.uid] = next[page.uid].map((obj) => (
            obj.role === NOTE_MARKER_ROLE && labels.has(obj.noteId)
                ? { ...obj, text: labels.get(obj.noteId), marks: null }
                : obj
        ));
    });

    pages.forEach((page, pageIndex) => {
        const mine = order
            .filter((note) => homeOf.get(note.noteId) === pageIndex)
            .map((note) => bodies.get(note.noteId))
            .filter(Boolean);
        if (!mine.length) return;

        const stackHeight = mine.length * NOTE_ROW;
        let y = page.height - BOTTOM_MARGIN - stackHeight;

        next[page.uid].push({
            ...makeShape(OBJ.LINE, { x: SIDE_MARGIN, y: y - 8, w: 140, h: 0 }, {
                stroke: '#6B635C',
                strokeWidth: 0.6,
                fill: 'transparent',
            }),
            role: NOTE_RULE_ROLE,
        });

        mine.forEach((body) => {
            next[page.uid].push({
                ...body,
                x: SIDE_MARGIN,
                y,
                w: page.width - SIDE_MARGIN * 2,
                h: NOTE_ROW,
                text: withLabel(labels.get(body.noteId), noteTextOf(body)),
            });
            y += NOTE_ROW;
        });
    });

    return next;
}

/** The note body that belongs to a marker, wherever on the document it ended up. */
export function findNoteBody(pages, objectsByUid, noteId) {
    for (const [pageIndex, page] of pages.entries()) {
        const body = (objectsByUid[page.uid] || [])
            .find((obj) => obj.role === NOTE_BODY_ROLE && obj.noteId === noteId);
        if (body) return { body, pageIndex, uid: page.uid };
    }
    return null;
}
